<template>
    <van-popup v-model="showPopup" position="bottom" round :style="{ height: '80%' }" :close-on-click-overlay="false">
        <div class="word-stat-popup">
            <!-- 标题 -->
            <div class="popup-header">
                <h3 class="popup-title">词根饱和度查询</h3>
                <van-icon name="cross" class="close-icon" @click="handleClose" />
            </div>

            <!-- 词根列表 -->
            <div class="word-list">
                <div class="word-item" v-for="(item, index) in wordStatList" :key="index">
                    <!-- 词根输入行 -->
                    <div class="word-input-row">
                        <span class="word-index">{{ index + 1 }}</span>
                        <van-field
                            v-model="item.word"
                            placeholder="请输入词根"
                            class="word-field"
                            :border="false"
                            :ref="'wordField' + index"
                            @input="handleWordChange(index)"
                        />
                        <van-button
                            v-if="item.showQueryBtn"
                            size="mini"
                            type="primary"
                            class="query-btn"
                            @click="handleQuerySingle(index)"
                            :loading="item.queryLoading"
                        >
                            查询
                        </van-button>
                    </div>

                    <!-- 状态标签 -->
                    <div class="word-status" v-if="item.statusText !== '未查询' || item.queryLoading" :class="{
                        'saturated': item.isSaturated,
                        'unsaturated': !item.isSaturated && item.statusText !== '未查询',
                        'querying': item.queryLoading
                    }">
                        <span class="status-dot"></span>
                        <span class="status-text">{{ item.statusText }}</span>
                    </div>
                </div>

                <!-- 添加词根按钮 -->
                <div class="add-word-trigger" @click="handleAddWord">
                    <van-icon name="plus" class="add-icon" />
                    <span class="add-text">添加词根</span>
                </div>
            </div>

            <!-- 底部按钮 -->
            <div class="popup-footer">
                <van-button @click="handleClose" class="footer-btn close-btn" plain>
                    <van-icon name="cross" />
                </van-button>
                <van-button @click="handleContinue" class="footer-btn continue-btn" size="large">
                    <span>继续询价</span>
                    <van-icon name="search" />
                </van-button>
            </div>
        </div>
    </van-popup>
</template>

<script>
import { wordStatWithCache } from '@/api/quotation';

export default {
    name: 'WordSaturationPopup',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        wordList: {
            type: Array,
            default: () => []
        },
        category: {
            type: String,
            default: ''
        },
        subCategory: {
            type: String,
            default: ''
        },
        province: {
            type: String,
            default: ''
        },
        city: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            showPopup: false,
            wordStatList: [],
            queryLoading: false
        };
    },
    watch: {
        show(val) {
            this.showPopup = val;
            if (val) {
                this.initWordList();
            }
        },
        showPopup(val) {
            this.$emit('update:show', val);
        }
    },
    methods: {
        // 初始化词根列表
        initWordList() {
            this.wordStatList = (this.wordList || []).map(word => ({
                word,
                count: 0,
                isSaturated: false,
                statusText: '未查询',
                showQueryBtn: false,
                queryLoading: false
            }));

            // 自动查询
            this.queryWordStat();
        },

        // 词根修改
        handleWordChange(index) {
            this.wordStatList[index].showQueryBtn = true;
            this.wordStatList[index].statusText = '未查询';
            this.wordStatList[index].isSaturated = false;
        },

        // 查询单个词根
        handleQuerySingle(index) {
            const word = this.wordStatList[index].word.trim();
            if (!word) {
                this.$msg.error('请输入词根');
                return;
            }

            this.wordStatList[index].queryLoading = true;

            wordStatWithCache([word], this.category, this.subCategory, this.province, this.city)
                .then(res => {
                    const statData = res ||  (new Map());
                    const stat = statData.get(word) || {};

                    if (stat) {
                        this.wordStatList[index].count = stat.count || 0;
                        this.wordStatList[index].similarCount = stat.similarCount || 0;
                        this.wordStatList[index].isSaturated = !stat.available;
                        this.wordStatList[index].statusText = stat.description || '未饱和';
                    }

                    this.wordStatList[index].showQueryBtn = false;
                })
                .catch(err => {
                    this.$msg.error(err || '查询失败');
                })
                .finally(() => {
                    this.wordStatList[index].queryLoading = false;
                });
        },

        // 查询词根统计
        queryWordStat() {
            const words = this.wordStatList.map(item => item.word).filter(w => w.trim());

            if (!words.length) {
                this.$msg.error('请输入词根');
                return;
            }

            this.queryLoading = true;

            wordStatWithCache(words, this.category, this.subCategory, this.province, this.city)
                .then(res => {
                    const statData = res ||  (new Map());

                    // 更新统计结果
                    this.wordStatList.forEach(item => {
                        const stat = statData.get(item.word) || {};
                        if (stat) {
                            item.count = stat.count || 0;
                            item.similarCount = stat.similarCount || 0;
                            item.isSaturated = !stat.available;
                            item.statusText = stat.description || '未饱和';
                            item.showQueryBtn = false;
                        }
                    });
                })
                .catch(err => {
                    this.$msg.error(err || '查询失败');
                })
                .finally(() => {
                    this.queryLoading = false;
                });
        },

        // 关闭弹框
        handleClose() {
            // 通知父组件更新词根数组
            const updatedWords = this.wordStatList.map(item => item.word).filter(w => w.trim());
            this.$emit('update-words', updatedWords);

            this.showPopup = false;
            this.$emit('close');
        },

        // 添加词根
        handleAddWord() {
            this.wordStatList.push({
                word: '',
                count: 0,
                isSaturated: false,
                statusText: '未查询',
                showQueryBtn: false,
                queryLoading: false
            });

            // 让新增的输入框获得焦点
            this.$nextTick(() => {
                const newIndex = this.wordStatList.length - 1;
                const fieldRef = this.$refs['wordField' + newIndex];
                if (fieldRef && fieldRef.focus) {
                    fieldRef.focus();
                }
            });
        },

        // 重新查询
        handleRequery() {
            this.queryWordStat();
        },

        // 继续询价
        handleContinue() {
            // 通知父组件更新词根数组
            const updatedWords = this.wordStatList.map(item => item.word).filter(w => w.trim());
            this.$emit('update-words', updatedWords);

            this.showPopup = false;
            this.$emit('continue');
        }
    }
};
</script>

<style scoped>
/* ============================================================
   DESIGN TOKENS (Purple theme)
   ============================================================ */
.word-stat-popup {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    color: #0d253d;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e3e8ee;
}

.popup-title {
    margin: 0;
    font-size: 1.2em;
    font-weight: 500;
    color: #0d253d;
}

.close-icon {
    font-size: 1.5em;
    color: #64748d;
    cursor: pointer;
    transition: all 0.3s ease;
}

.close-icon:hover {
    color: #0d253d;
}

.word-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #f6f9fc;
}

.word-item {
    background: #fff;
    border: 1px solid #e3e8ee;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 10px;
}

.word-item:hover {
    border-color: #533afd;
}

.word-input-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.word-index {
    width: 22px;
    height: 22px;
    background: #f0edff;
    color: #533afd;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}

.word-field {
    flex: 1;
}

.word-field /deep/ .van-field__control {
    color: #0d253d !important;
    font-size: 14px;
}

.word-field /deep/ .van-field__control::placeholder {
    color: #a8c3de !important;
}

.query-btn {
    flex-shrink: 0;
    height: 28px !important;
    padding: 0 10px !important;
    font-size: 12px !important;
    background: #533afd !important;
    border-color: #533afd !important;
}

.word-status {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}

.status-text {
    font-weight: 500;
    color: #64748d;
}

.word-status.saturated {
    background: #fff1f5;
}

.word-status.saturated .status-dot {
    background: #ea2261;
    box-shadow: 0 0 4px #ea2261;
}

.word-status.saturated .status-text {
    color: #c4134e;
}

.word-status.unsaturated {
    background: #f6ffed;
}

.word-status.unsaturated .status-dot {
    background: #52c41a;
    box-shadow: 0 0 4px #52c41a;
}

.word-status.unsaturated .status-text {
    color: #389e0d;
}

.word-status.querying {
    background: #f0edff;
}

.word-status.querying .status-dot {
    background: #533afd;
    animation: blink 1s ease-in-out infinite;
}

@keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
}

.popup-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: #fff;
    border-top: 1px solid #e3e8ee;
}

.footer-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.close-btn {
    width: 40px;
    height: 40px;
    border-radius: 50% !important;
    background: #f6f9fc !important;
    border: 1px solid #e3e8ee !important;
    color: #64748d !important;
}

.close-btn:active {
    background: #e3e8ee !important;
}

.close-btn /deep/ .van-icon {
    font-size: 18px;
}

.continue-btn {
    flex: 1;
    max-width: 280px;
    height: 44px;
    border-radius: 22px !important;
    background: linear-gradient(135deg, #533afd, #4434d4) !important;
    border: none !important;
    color: #fff !important;
    font-size: 15px !important;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(83, 58, 253, 0.35);
    margin-left: 12px;
}

.continue-btn:active {
    transform: scale(0.98);
    box-shadow: 0 2px 8px rgba(83, 58, 253, 0.3);
}

.continue-btn /deep/ .van-icon {
    font-size: 16px;
    margin-left: 4px;
}

/* 滚动条样式 */
.word-list::-webkit-scrollbar {
    width: 6px;
}

.word-list::-webkit-scrollbar-track {
    background: #f6f9fc;
    border-radius: 3px;
}

.word-list::-webkit-scrollbar-thumb {
    background: #a8c3de;
    border-radius: 3px;
}

.word-list::-webkit-scrollbar-thumb:hover {
    background: #64748d;
}

.add-word-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    margin-top: 8px;
    border: 2px dashed #a8c3de;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #fafbff;
}

.add-word-trigger:hover {
    border-color: #533afd;
    background: #f0edff;
}

.add-word-trigger:active {
    transform: scale(0.98);
}

.add-icon {
    font-size: 20px;
    color: #533afd;
}

.add-text {
    font-size: 14px;
    color: #64748d;
    font-weight: 500;
}
</style>
